/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * AngularJs component that displays list of deleted unit.
 */
angular.module('bulbApp.blockModule')
  .component('bulbDeletedUnitBlock', {
    templateUrl: 'ajs/components/block/deletedunitblock/deletedunitblock.html',
    bindings: {
      compositeUnit: '=',
    },
    controllerAs: 'deletedUnitBlockCtrl',
    controller: [
      '$animateCss',
      '$element',
      '$scope',
      '$state',
      '$timeout',
      '$translate',
      'iziToast',
      'BulbAuthService',
      'BulbBlockViewSwitcherService',
      'BulbBusyCoverService',
      'BulbDeletedUnitService',
      'BulbErrorService',
      'BulbModalService',
      'magnitudeFilter',
      'CONFIG',
      DeletedUnitBlockController,
    ],
  });

function DeletedUnitBlockController(
  $animateCss,
  $element,
  $scope,
  $state,
  $timeout,
  $translate,
  iziToast,
  BulbAuthService,
  BulbBlockViewSwitcherService,
  BulbBusyCoverService,
  BulbDeletedUnitService,
  BulbErrorService,
  BulbModalService,
  magnitudeFilter,
  CONFIG,
) {
  const that = this;
  this.deletedUnits = [];
  this.isSingleAuthor = (this.compositeUnit.unitType !== 'GroupUnit');
  this.isSingleType = (this.compositeUnit.unitType === 'CompositeUnit');
  this.restoreIndex = undefined;
  this.infoType = this.compositeUnit.unitType;
  this.collectionId = undefined;

  /**
   * Modal options for deleted unit when user click on delete button.
   */
  const deletedModalOption = {
    fontAwesomeCode: 'fas fa-trash-alt',
    titleCode: 'permanent_delete_heading',
    bodyCode: 'permanent_delete_body',
    okButtonCode: 'button_delete',
    showCancelButton: true,
    windowClass: 'updated-modal-design red-button-updated-modal bulb-permanent-deleted-modal',
  };

  /**
   * Modal options for storage limit exceed modal.
   */
  const storageLimitReachedModalOption = {
    fontAwesomeCode: 'fa fa-exclamation-triangle',
    titleCode: 'soft_delete_storage_limit_heading',
    bodyText: $translate.instant(
      'soft_delete_storage_limit_body',
      {
        storage: magnitudeFilter(BulbAuthService.currentUser.features.StorageFeature.maxStorageBytes, { factor: 1024 }),
      },
    ),
    okButtonCode: 'button_upgrade',
    cancelButtonCode: 'button_view_media',
    windowClass: 'updated-modal-design bulb-snapshot-pdf bulb-restore-modal',
  };

  /**
   * Initialize required variables on component load.
   */
  this.$onInit = function () {
    this.currentUser = BulbAuthService.currentUser;
    this.currentUserPrefs = that.currentUser.prefs;
    this.isAdmin = BulbDeletedUnitService.isAdmin();
    $scope.CONFIG = CONFIG;
    $scope.viewSwitcherService = BulbBlockViewSwitcherService;
    this.permanentlyDeleteInfo = $translate.instant(
      'soft_delete_after_days',
      { days: BulbDeletedUnitService.getDaysForDelete() },
    );
    this.getDeletedUnitList();
  };

  /**
   * Show toast message on delete and recover.
   * @param actionType {string} Deleted or restore.
   * @param unitType {string} Type of unit.
   * @param path {string} Url of unit.
   */
  this.showToastMessage = function (actionType, unitType, path) {
    let message = '';
    if (actionType === 'restore') {
      const unitPathUrl = $translate.instant('soft_delete_view_now_text');
      message = `${$translate.instant('soft_delete_unit_restore_toast', { unitType })
      }<br/><a href="u/${path}"> ${unitPathUrl}</a></span>`;
    } else {
      message = $translate.instant('soft_delete_unit_delete_toast', { unitType });
    }
    iziToast.show({ // jshint ignore:line
      theme: 'dark',
      timeout: 5000,
      close: false,
      icon: 'far fa-trash-restore-alt',
      message,
      position: 'bottomRight',
      progressBarColor: 'rgb(0,174,239)', // $bulb-blue-color
    });
    $scope.viewSwitcherService = BulbBlockViewSwitcherService;
  };

  /**
   * Call api to delete unit when user click on delete button.
   * @param unit Unit to be deleted.
   */
  this.delete = function (unit) {
    BulbModalService.show(deletedModalOption).then((result) => {
      const mode = unit.softDelete ? 'force' : 'permanent';
      BulbBusyCoverService.showBusyDuring(
        BulbDeletedUnitService.delete(unit.id, mode).then((result) => {
          that.showToastMessage('delete', getBlockType(unit));
          that.getDeletedUnitList();
        }, (error) => {
          BulbErrorService.showError(error);
        }),
      );
    }, (error) => {
      if (!(error !== 'cancel' || error !== 'backdrop click')) {
        BulbErrorService.showError('Error occurred during delete', error);
      }
    });
  };

  /**
   * Restore deleted unit when user click on delete.
   *
   * @param unitId Id of unit will be restore
   * @param index Position of element which is getting restored.
   * @param softDelete Current delete status of unit.
   * @param unpublished Unit will be only in unpublished mode or not.
   */
  this.restore = function (unitId, index, softDelete, unpublished) {
    that.restoreIndex = index;
    animateBlock(softDelete);
    BulbDeletedUnitService.restore(unitId, unpublished).then((restoredUnit) => {
      that.showToastMessage('restore', getBlockType(restoredUnit), restoredUnit.path);
      that.getDeletedUnitList();
      BulbDeletedUnitService.fetchAuthorUnits();
    }, (error) => {
      if (error.data && error.data.developerMessage) {
        if (error.data.developerMessage === 'storage_limit_reached') {
          BulbModalService.show(storageLimitReachedModalOption).then(() => {
            BulbAuthService.planlistState();
          }, (error) => {
            if (error === 'cancel') {
              $state.go('assetlibrary');
            }
          });
        }
      } else {
        BulbErrorService.showError('Error occurred during restore', error);
      }
    });
  };

  /**
   * Navigate home method for deleted unit bread crumb.
   */
  this.navHome = function navHome() {
    BulbDeletedUnitService.isRecentlyDeletedSelected = false;
    BulbDeletedUnitService.navigateToAuthor = true;
  };

  /**
   * Will be execute when deleted collection will be opened.
   */
  $scope.$on('openDeletedCollection', (event, deletedUnit) => {
    that.infoType = deletedUnit.unitType;
    that.collectionId = deletedUnit.id;
    that.getDeletedUnitList();
  });

  /**
   * Determines a block type from the given unit
   * @param unit
   * @returns String a string constant representing this block's type
   */
  function getBlockType(unit) {
    switch (unit.unitType) {
      case 'CompositeUnit':
        return 'Collection';
      case 'DraftPageUnit':
      case 'PublishedPageUnit':
      case 'ArchivePageUnit':
        return 'Page';
      default:
        return unit.unitType;
    }
  }

  /**
   * Get list of deleted units.
   */
  this.getDeletedUnitList = function () {
    if (that.infoType === 'UserUnit' || that.infoType === 'DeletedUserUnit') {
      this.collectionId = undefined;
      $scope.$parent.showBreadcrumbForDeletedUnit(that.compositeUnit, that.infoType);
      BulbBusyCoverService.showBusyDuring(
        BulbDeletedUnitService.getDeletedUnits(BulbDeletedUnitService.authorAccountId).then((result) => {
          that.deletedUnits = result;
        }, (error) => {
          BulbErrorService.showError(error);
        }),
      );
    } else if (that.infoType === 'CompositeUnit' && that.collectionId) {
      BulbBusyCoverService.showBusyDuring(
        BulbDeletedUnitService.getDeletedUnit(that.collectionId).then((result) => {
          that.deletedUnits = [];
          if (result) {
            const deletedUnit = result.unitDTOs[0];
            // deleted unit doesn't have parent if unit parent is not deleted.
            const userUnit = {
              id: that.compositeUnit.id,
              unitType: that.compositeUnit.unitType,
              path: that.compositeUnit.path,
              ownerDisplayName: that.compositeUnit.ownerDisplayName,
              ownerUsername: that.compositeUnit.ownerUsername,
            };
            deletedUnit.ancestorUnits.splice(0, 0, userUnit);
            that.deletedUnits = deletedUnit.children;
            $scope.$parent.showBreadcrumbForDeletedUnit(deletedUnit);
          }
        }, (err) => {
          BulbErrorService.showError(err);
        }),
      );
    }
  };

  /**
   * Add animation for tile view.
   */
  function animateTileBlock(softDelete) {
    const elements = $element.find(softDelete ? '.bulb-soft-delete-tile' : '.bulb-hard-delete-tile');
    const elementId = `#block-tile${softDelete ? '-soft' : '-hard'}`;
    if (elements && (elements.length - 1) !== that.restoreIndex) {
      for (let index = 0; index < elements.length; index++) {
        if (index > that.restoreIndex) {
          const element = $element.find(elementId + index);
          let animator;
          if (index % 4 !== 0) {
            const elementWidth = $element.find(elementId + (index > 0 ? (index - 1) : 0)).width();
            animator = $animateCss(element, {
              from: { right: '0px' },
              to: { right: `${elementWidth + 15}px` }, // add 15 margin to the cover.
              duration: 1, // 5 second
            });
            animator.start();
          } else {
            element.css({ position: 'relative' });
            element.animate({
              opacity: 0,
              left: -20,
            }, 1000, () => {
            });
          }
        }
      }
    }
  }

  /**
   * Add animation for list view.
   */
  function animateListBlock(softDelete) {
    const elements = $element.find(softDelete ? '.bulb-soft-delete-list' : '.bulb-hard-delete-list');
    const elementId = `#block-list${softDelete ? '-soft' : '-hard'}`;
    if (elements && (elements.length - 1) !== that.restoreIndex) {
      for (let index = 0; index < elements.length; index++) {
        if (index > that.restoreIndex) {
          const element = $element.find(elementId + index);
          const elementHeight = ($element.find(elementId + index).offset().top - $element.find(elementId + (index - 1)).offset().top);
          const animator = $animateCss(element, {
            addClass: 'fadeOutTop',
            from: { top: '0px' },
            to: { top: `${-(elementHeight)}px` }, // add 32 pm to cover 2em margin
            duration: 3, // 3 second
          });
          animator.start();
        }
      }
    }
  }

  /**
   * Add animation on restore unit.
   */
  function animateBlock(softDelete) {
    const isTileView = $scope.viewSwitcherService.isTile('profile');
    const baseId = `#block-${isTileView ? 'tile' : 'list'}${softDelete ? '-soft' : '-hard'}`;

    // Ensure restoreIndex exists
    if (that.restoreIndex == null) {
      console.warn('animateBlock: restoreIndex is missing');
      return;
    }

    // Build the final selector
    const selector = baseId + that.restoreIndex;

    // Delay to ensure DOM is updated (if element might be newly rendered)
    $timeout(() => {
      const element = $element.find(selector);

      // Ensure the element exists
      if (!element || !element.length) {
        console.warn(`animateBlock: element not found for selector ${selector}`);
        return;
      }

      // Animate safely
      $animateCss(element, {
        addClass: 'fadeInAndOut',
      }).start();

      // Continue with view-specific animation
      if (isTileView) {
        animateTileBlock(softDelete);
      } else {
        animateListBlock(softDelete);
      }
    });
  }
}
